import { debounce } from './theme.js';

const DESKTOP_QUERY = '(min-width: 990px)';

export class CollectionInfo extends HTMLElement {
  constructor() {
    super();
    this.debounceOnChange = debounce((event) => this.onChangeHandler(event), 800);
    this.addEventListener('change', this.debounceOnChange.bind(this));
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  onClickHandler = (event) => {
    if (event.target.matches('[data-render-section-url]')) {
      event.preventDefault();
      const searchParams = new URLSearchParams(event.target.dataset.renderSectionUrl.split('?')[1]).toString();
      this.fetchSection(searchParams);
    }
  };

  onChangeHandler = (event) => {
    if (!event.target.matches('[data-render-section]')) return;

    const form = event.target.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const existingParams = new URLSearchParams(window.location.search);
    const qValue = existingParams.get('q');

    this.removeDefaultPriceFilters(searchParams, form);

    const finalParams = qValue ? `q=${encodeURIComponent(qValue)}&${searchParams}` : searchParams.toString();
    this.fetchSection(finalParams);
  };

  get form() {
    return this.querySelector('collection-info form');
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  removeDefaultPriceFilters = (searchParams, form) => {
    const priceMin = searchParams.get('filter.v.price.gte');
    const priceMax = searchParams.get('filter.v.price.lte');
    const actualMaxPrice = form.querySelector('.max-range')?.getAttribute('max');

    const isMinAtDefault = priceMin === '0' || !priceMin;
    const isMaxAtDefault = !actualMaxPrice || priceMax === actualMaxPrice || !priceMax;

    if (isMinAtDefault && isMaxAtDefault) {
      searchParams.delete('filter.v.price.gte');
      searchParams.delete('filter.v.price.lte');
    }
  };

  updateURL(searchParams) {
    history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
  }

  updateFilters = (html, className) => {
    const filtersFromFetch = html.querySelectorAll(`collection-info .${className}`);
    const filtersFromDom = document.querySelectorAll(`collection-info .${className}`);

    // Remove filters that are no longer returned from the server
    Array.from(filtersFromDom).forEach((currentElement) => {
      if (!Array.from(filtersFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    Array.from(filtersFromFetch).forEach((elementToRender, index) => {
      document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
    });
  };

  showLoadingOverlay = () => {
    const loadingOverlay = this.querySelector(`#loading-overlay-${this.dataset.section}`);
    const loadingSpinner = this.querySelector(`#loading-spinner-${this.dataset.section}`);
    const resultsCount = this.querySelector(`#results-count-${this.dataset.section}`);
    const drawerResultsCount = this.querySelector(`#drawer-results-count-${this.dataset.section}`);
    const drawerLoadingSpinner = this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`);

    if (loadingOverlay) loadingOverlay.style.display = 'flex';
    if (loadingSpinner) loadingSpinner.style.display = 'block';
    if (resultsCount) resultsCount.style.display = 'none';
    if (drawerResultsCount) drawerResultsCount.style.display = 'none';
    if (drawerLoadingSpinner) drawerLoadingSpinner.style.display = 'block';
  };

  hideLoadingOverlay = () => {
    const loadingOverlay = this.querySelector(`#loading-overlay-${this.dataset.section}`);
    const loadingSpinner = this.querySelector(`#loading-spinner-${this.dataset.section}`);
    const resultsCount = this.querySelector(`#results-count-${this.dataset.section}`);
    const drawerResultsCount = this.querySelector(`#drawer-results-count-${this.dataset.section}`);
    const drawerLoadingSpinner = this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`);

    if (loadingOverlay) loadingOverlay.style.display = 'none';
    if (loadingSpinner) loadingSpinner.style.display = 'none';
    if (resultsCount) resultsCount.style.display = 'block';
    if (drawerResultsCount) drawerResultsCount.style.display = 'block';
    if (drawerLoadingSpinner) drawerLoadingSpinner.style.display = 'none';
  };

  fetchSection = (searchParams) => {
    this.showLoadingOverlay();
    fetch(`${window.location.pathname}?section_id=${this.dataset.section}&${searchParams}`)
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateURL(searchParams);
        this.updateSourceFromDestination(html, `product-grid-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `drawer-results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filters-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filter-group-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-drawer-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `filters-drawer-buttons-wrapper-id`);
        this.updateFilters(html, `js-filter`);
        this.hideLoadingOverlay();
        this.scrollToProductGrid();
      })
      .catch((error) => {
        console.error(error);
        this.hideLoadingOverlay();
      });
  };

  scrollToProductGrid = () => {
    const productGrid = this.querySelector(`#product-grid-${this.dataset.section}`);
    const headerId = document.querySelector('#main-header');
    const headerOffset = (headerId.offsetHeight + 10) || 80;

    if (productGrid) {
      const elementPosition = productGrid.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
}

if (!customElements.get('collection-info')) {
  customElements.define('collection-info', CollectionInfo);
}

// CollectionCarousel stays a distinct registered custom element (not folded into
// CollectionInfo) so it auto-upgrades on every AJAX-replaced product-grid render, the
// same zero-glue mechanism component-product-card.js already relies on in this same
// region. See "CollectionCarousel: one file, two custom elements" in
// docs/base-theme-standards/base-theme-decisions-log.md for why this deviates from
// Base's usual one-file-one-class-per-section pattern.
if (!customElements.get('collection-carousel')) {
  customElements.define(
    'collection-carousel',
    class CollectionCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        this.cards = Array.from(this.querySelectorAll('[data-carousel-card]'));

        if (!this.track || this.cards.length === 0) { return; }

        this.desktop = window.matchMedia(DESKTOP_QUERY);
        this.listenersAttached = false;

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onDotClick = this.onDotClick.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.desktop.addEventListener('change', this.onBreakpointChange);
        this.syncListeners();
      }

      disconnectedCallback() {
        this.desktop?.removeEventListener('change', this.onBreakpointChange);
        this.teardownListeners();
      }

      onBreakpointChange() {
        this.syncListeners();
      }

      // Attach/detach scroll, resize, and dot-click listeners based on the current
      // breakpoint so nothing runs above 990px, where dots are hidden and the
      // track becomes a static CSS grid (no scrolling to track).
      syncListeners() {
        if (this.desktop.matches) {
          this.teardownListeners();
          return;
        }

        this.setupListeners();
      }

      setupListeners() {
        if (this.listenersAttached) { return; }

        this.track.addEventListener('scroll', this.onScroll, {passive: true});
        window.addEventListener('resize', this.onResize);

        for (const dot of this.dots) {
          dot.addEventListener('click', this.onDotClick);
        }

        this.listenersAttached = true;
        this.updateDots();
      }

      teardownListeners() {
        if (!this.listenersAttached) { return; }

        this.track?.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);

        for (const dot of this.dots) {
          dot.removeEventListener('click', this.onDotClick);
        }

        this.listenersAttached = false;
      }

      onResize() {
        this.updateDots();
      }

      onScroll() {
        window.requestAnimationFrame(() => this.updateDots());
      }

      onDotClick(event) {
        const index = Number(event.currentTarget.dataset.index);
        const card = this.cards[index];
        if (!card || !this.track) { return; }

        this.track.scrollTo({
          left: card.offsetLeft,
          behavior: 'smooth',
        });
      }

      updateDots() {
        if (!this.track || this.dots.length === 0) { return; }

        const scrollLeft = this.track.scrollLeft;
        let activeIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = index;
          }
        });

        this.dots.forEach((dot, index) => {
          const isActive = (index === activeIndex);
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }
    },
  );
}